import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { applicationApi } from "api/application";
import { BaseOption, InputSize, Select } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { useAuthContext } from "providers/Auth";
import { useMemo } from "react";
import { toast } from "react-toastify";
import { User } from "views/settings-users/types";
import { useSortedAssignees } from "../hooks/useSortedAssignees";
import { HugeiconsIcon } from "@hugeicons/react";
import { UserMinus01Icon } from "@hugeicons-pro/core-stroke-rounded";

interface AssigneeSelectProps {
  value?: number | null;
  rowId?: string;
  inputClassName?: string;
  onChange?: (partner_user_id: number | null) => void;
  label?: string;
  size?: InputSize;
  mode?: "filter" | "assign";
}

type AssigneeUser = Pick<User, "id" | "name">;
type AssigneeOption = BaseOption & {
  kind: "any" | "user" | "unassigned";
  user?: AssigneeUser;
};

export const AssigneeSelect = ({
  value,
  rowId,
  inputClassName,
  onChange,
  label,
  size = InputSize.SM,
  mode = "assign"
}: AssigneeSelectProps) => {
  const queryClient = useQueryClient();
  const { auth } = useAuthContext();

  const { data: assignees, isLoading } = useQuery({
    queryKey: ["APPLICATION_ASSIGNEES"],
    queryFn: () => applicationApi.getAssignees()
  });

  const { mutateAsync } = useMutation({
    mutationKey: ["APPLICATION_ASSIGN"],
    mutationFn: ({
      application_id,
      partner_user_id
    }: {
      application_id: string;
      partner_user_id: number | null;
    }) =>
      applicationApi.assignApplication({
        application_id: Number(application_id),
        assignee_ids: partner_user_id !== null ? [partner_user_id] : []
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["APPLICATION_ACTIVITY_LOG"] });
      queryClient.invalidateQueries({ queryKey: ["APPLICANTS_TABLE"] });
      if (rowId)
        queryClient.invalidateQueries({ queryKey: ["APPLICATION", rowId] });
      toast.success("Application assigned successfully");
    },
    onError: () => {
      toast.error("Failed to assign application");
    }
  });

  const sortedAssignees = useSortedAssignees(assignees);

  const handleAssigneeChange = (userId: number | null) => {
    if (onChange) onChange(userId);
    else if (rowId)
      mutateAsync({
        application_id: String(rowId),
        partner_user_id: userId
      });
  };

  const selectValue = useMemo(
    () => value ?? (mode === "filter" ? "any" : undefined),
    [mode, value]
  );

  const options = useMemo<AssigneeOption[]>(() => {
    const baseOptions: AssigneeOption[] =
      mode === "filter"
        ? [{ label: "Any", value: "any", kind: "any" }]
        : [{ label: "No assignee", value: "unassigned", kind: "unassigned" }];

    return [
      ...baseOptions,
      ...sortedAssignees.map((assignee) => ({
        label: assignee.name,
        value: assignee.id,
        id: assignee.id,
        kind: "user" as const,
        user: {
          id: assignee.id,
          name: assignee.name
        }
      }))
    ];
  }, [mode, sortedAssignees]);

  const optionsKey = useMemo(() => {
    return `${options.length}-${auth?.id ?? "no-auth"}-${mode}`;
  }, [options, auth, mode]);

  return (
    <div
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className={inputClassName || "w-full"}
    >
      {label && (
        <label className="block text-sm font-medium text-content-1000 mb-1">
          {label}
        </label>
      )}
      <Select
        key={optionsKey}
        id={`assigned-to-select-${rowId || "filter"}`}
        size={size}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        className={cx("w-full", inputClassName)}
        value={selectValue}
        options={options}
        isDisabled={isLoading}
        placeholder="Unassigned"
        legacyErrorMargins={false}
        styles={{
          valueContainer: (base) => ({
            ...base,
            alignItems: "center",
            paddingTop: 0,
            paddingBottom: 0
          }),
          singleValue: (base) => ({
            ...base,
            margin: 0,
            maxWidth: "100%",
            display: "flex",
            alignItems: "center"
          })
        }}
        onChange={(e) => {
          const nextValue = e.target.value;
          if (onChange) {
            if (nextValue === "any") return handleAssigneeChange(null);
            return handleAssigneeChange(Number(nextValue));
          }
          if (nextValue === "unassigned") return handleAssigneeChange(null);
          handleAssigneeChange(Number(nextValue));
        }}
        formatOptionLabel={(option, meta) => {
          const assigneeOption = option as AssigneeOption;
          if (meta.context === "value") {
            if (assigneeOption.kind === "user" && assigneeOption.user)
              return (
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar
                    name={assigneeOption.user.name}
                    className="text-[9px] font-black"
                    size={20}
                  />
                  <span className="text-base font-medium text-content-1000 truncate min-w-0">
                    {assigneeOption.user.name}
                  </span>
                  {assigneeOption.user.id === auth?.id ? (
                    <span className="text-base font-medium text-content-200 whitespace-nowrap shrink-0">
                      (You)
                    </span>
                  ) : null}
                </div>
              );
            if (assigneeOption.kind === "unassigned")
              return (
                <div className="flex items-center gap-2 min-w-0">
                  <HugeiconsIcon
                    icon={UserMinus01Icon}
                    className="text-content-200"
                    size={20}
                  />
                  <span className="block w-full truncate text-base font-medium text-content-200">
                    {assigneeOption.label as string}
                  </span>
                </div>
              );
            return (
              <span className="block w-full truncate text-base font-medium text-content-200">
                {assigneeOption.label as string}
              </span>
            );
          }
          if (assigneeOption.kind === "user" && assigneeOption.user)
            return (
              <div className="flex items-center gap-2 py-1 min-w-0">
                <Avatar
                  name={assigneeOption.user.name}
                  className="text-[9px] font-black"
                  size={20}
                />
                <span className="text-base font-medium text-content-1000 truncate min-w-0">
                  {assigneeOption.user.name}
                </span>
                {assigneeOption.user.id === auth?.id ? (
                  <span className="text-base font-medium text-content-200 whitespace-nowrap">
                    (You)
                  </span>
                ) : null}
              </div>
            );

          if (assigneeOption.kind === "unassigned")
            return (
              <div className="flex items-center gap-2 py-1">
                <HugeiconsIcon
                  icon={UserMinus01Icon}
                  className="text-content-200"
                  size={20}
                />
                <span className="text-base font-medium text-content-200">
                  {assigneeOption.label as string}
                </span>
              </div>
            );

          return (
            <div className="flex items-center gap-2 py-1">
              <span className="text-base font-medium text-content-200">
                {assigneeOption.label as string}
              </span>
            </div>
          );
        }}
      />
    </div>
  );
};
